import { z } from 'zod';
import { TimestampSchema } from './primitives.js';

/**
 * `contactInquiries/{id}` — messages submitted from the public marketing
 * `/contact` form. Server-written (via the `submitInquiry` callable), read by
 * super-admin on a future moderation page. Kept minimal + PII-light on purpose.
 */

export const ContactInquiryStatusSchema = z
  .enum(['new', 'read', 'responded', 'spam'])
  .describe('Moderation lifecycle for a contact inquiry.');
export type ContactInquiryStatus = z.infer<typeof ContactInquiryStatusSchema>;

/**
 * Agent-side progress on a routed lead.
 *
 * Separate from `ContactInquiryStatus`: that tracks whether the platform has
 * dealt with the MESSAGE, this tracks whether the agent has worked the
 * OPPORTUNITY. Collapsing them would let "we read it" read as "someone is
 * selling to them".
 */
export const LeadStatusSchema = z
  .enum(['assigned', 'contacted', 'won', 'lost'])
  .describe('Agent-side lifecycle for a routed lead.');
export type LeadStatus = z.infer<typeof LeadStatusSchema>;

export const ContactInquirySchema = z.object({
  id: z.string().describe('Firestore document id.'),
  name: z.string().min(1).max(100).describe("Sender's name as entered."),
  email: z.string().email().describe("Sender's email for the reply."),
  message: z.string().min(1).max(2000).describe('The inquiry body.'),
  locale: z
    .enum(['en', 'ms', 'zh', 'ta'])
    .default('en')
    .describe('UI locale the form was submitted in (routes the reply language).'),
  source: z
    .literal('marketing_contact')
    .default('marketing_contact')
    .describe('Which surface produced the inquiry (only the marketing form for now).'),
  platformBrandId: z
    .string()
    .min(1)
    .describe('Platform brand the inquiry belongs to (multi-brand routing).'),
  status: ContactInquiryStatusSchema.default('new').describe('Moderation status; defaults to new.'),

  /**
   * Whether the sender asked to be contacted by a local representative.
   *
   * This is the consent gate for the whole agent-lead path (G25). Writing to a
   * company is not the same as agreeing to be called by a commissioned
   * salesperson, so an inquiry without this tick is never routed to an agent —
   * it stays with the platform. Defaults false so an older document, or a
   * caller that omits the field, can never be treated as consent.
   */
  wantsRepresentativeContact: z
    .boolean()
    .default(false)
    .describe('Sender ticked "have a representative contact me"; gates agent routing.'),

  /** Set once a lead has been routed. Absent means nobody has been given it. */
  assignedAgentId: z
    .string()
    .optional()
    .describe('Agent this lead was routed to; absent while unrouted.'),
  assignedAt: TimestampSchema.optional().describe('When the lead was routed.'),
  assignmentReason: z
    .enum(['first_win_window', 'language_match', 'lightest_load'])
    .optional()
    .describe('Why this agent was chosen; shown to the agent, so it must be true.'),
  assignmentLanguageMatch: z
    .boolean()
    .optional()
    .describe(
      'False when no eligible agent spoke the sender language, so the UI can warn the agent rather than let them call blind.',
    ),

  /**
   * How far the lead has progressed. Distinct from `status`, which is the
   * platform's moderation view of the MESSAGE; this is the agent's view of the
   * OPPORTUNITY. A lead the agent has not touched must never read as handled.
   */
  leadStatus: LeadStatusSchema.optional().describe('Agent-side progress once routed.'),
  createdAt: TimestampSchema.describe('Server timestamp when the inquiry was stored.'),
});
export type ContactInquiry = z.infer<typeof ContactInquirySchema>;
